import { motion } from "framer-motion";
import { ShieldAlert, Flame, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1648555394313-494797ad48fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxvaWwlMjByaWclMjBuaWdodHxlbnwwfHx8fDE3Nzg3MjQwNDJ8MA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) brightness(0.4)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />

      <div className="relative z-10 max-w-6xl mx-auto px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Flame className="w-7 h-7 text-amber-500" />
            <div className="font-display text-2xl tracking-tight">{t("common.appName")}</div>
            <span className="font-mono-data text-[10px] tracking-[0.3em] text-text-secondary uppercase ml-2 hidden sm:inline">
              {t("common.tagline")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageCurrencySelector compact />
            <button
              onClick={() => navigate("/pricing")}
              data-testid="header-pricing-btn"
              className="font-mono-data text-xs tracking-widest uppercase text-text-secondary hover:text-white transition"
            >
              {t("nav.pricing")}
            </button>
            <button
              onClick={handleLogin}
              data-testid="header-login-btn"
              className="font-mono-data text-xs tracking-widest uppercase text-text-secondary hover:text-white transition"
            >
              {t("common.signIn")} →
            </button>
          </div>
        </div>

        <div className="mt-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="stamp stamp-warn mb-6 inline-flex">
              <ShieldAlert className="w-3 h-3" />
              {t("landing.kicker")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              {t("landing.heroLine1")} <span className="text-amber-500">{t("landing.heroLine2")}</span><br/>
              {t("landing.heroLine3")}
            </h1>
            <p className="mt-8 text-lg text-text-secondary max-w-xl leading-relaxed">
              {t("landing.heroSub")}
            </p>

            <div className="mt-12 flex items-center gap-4 flex-wrap">
              <button
                onClick={handleLogin}
                data-testid="get-started-btn"
                className="btn-tactile btn-amber inline-flex items-center gap-2 px-6 py-3"
              >
                {t("landing.ctaPrimary")}
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                data-testid="landing-pricing-btn"
                className="font-mono-data text-xs tracking-widest uppercase text-text-secondary hover:text-white"
              >
                {t("landing.ctaSecondary")} →
              </button>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                [t("landing.statAi"), t("landing.statAiSub")],
                [t("landing.statCategories"), t("landing.statCategoriesSub")],
                [t("landing.statApi"), t("landing.statApiSub")],
              ].map(([k, v]) => (
                <div key={k} className="border-l border-white/10 ps-4">
                  <div className="font-display text-xl text-amber-500">{k}</div>
                  <div className="font-mono-data text-[10px] tracking-[0.2em] text-text-secondary uppercase mt-1">{v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="tray p-7 relative">
              <div className="absolute -top-3 left-6 px-3 py-1 bg-amber-500/90 text-black text-[10px] font-mono-data tracking-widest uppercase rounded">
                {t("landing.livePriority")}
              </div>
              <div className="folder-3d folder-amber">
                <div className="folder-tab">RFP-2026-014</div>
                <div className="folder-body p-6 pt-8 min-h-[260px]">
                  <div className="font-display text-2xl mb-1">Permian Pipeline Tie-In</div>
                  <div className="font-mono-data text-xs text-text-secondary mb-6">Apache Corp · Deadline 28 Feb</div>
                  <div className="space-y-2 font-mono-data text-xs" dir="ltr">
                    {[
                      ["PIPING", "8\" Sch 80 Duplex 2205", "1,200 ft"],
                      ["INSTR", "Pressure Tx 0-1500psi", "6 ea"],
                      ["HSE", "H2S Monitors", "12 ea"],
                      ["LABOR", "Senior 6G Welder", "320 hr"],
                    ].map(([cat, desc, qty]) => (
                      <div key={desc} className="flex justify-between border-b border-white/5 pb-1">
                        <span className="text-amber-500/80 w-14">{cat}</span>
                        <span className="flex-1 px-2 text-text-secondary truncate">{desc}</span>
                        <span className="text-white">{qty}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 stamp stamp-warn">{t("landing.vendorsMissing")}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
